// Vendored from use-context-selector v1.4.1 (MIT) — https://github.com/dai-shi/use-context-selector
// React Context selector hooks — 仅导出 ChartSettings 所需 API

import {
  createElement,
  createContext as createContextOrig,
  useContext as useContextOrig,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import type {
  ComponentType,
  Context as ContextOrig,
  MutableRefObject,
  Provider,
  ReactNode,
} from 'react';
import {
  unstable_NormalPriority as NormalPriority,
  unstable_runWithPriority as runWithPriority,
} from 'scheduler';
import { bulkUpdate } from './batchedUpdates';

const DATA_SLOT = Symbol();
const PRIMITIVE_PROVIDER = Symbol();

const isServerEnv =
  typeof window === 'undefined' ||
  /ServerSideRendering/.test(
    window.navigator && (window.navigator as Navigator).userAgent,
  );

const safeUseLayoutEffect = isServerEnv ? useEffect : useLayoutEffect;

const invokeWithNormalPriority = runWithPriority
  ? (effect: () => void) => {
      try {
        runWithPriority(NormalPriority, effect);
      } catch (e) {
        if ((e as { message: unknown }).message === 'Not implemented.') {
          effect();
        } else {
          throw e;
        }
      }
    }
  : (effect: () => void) => effect();

type SnapshotId = number;
type Subscriber<Value> = (notification: {
  snap: SnapshotId;
  pending?: Promise<unknown>;
  val?: Value;
}) => void;

type StoreSlot<Value> = {
  [DATA_SLOT]: {
    valRef: MutableRefObject<Value>;
    snapRef: MutableRefObject<SnapshotId>;
    subsSet: Set<Subscriber<Value>>;
    schedule: (effect: () => void, settings?: { suspense: boolean }) => void;
  };
};

export interface IManagedContext<Value> {
  Provider: ComponentType<{ value: Value; children: ReactNode }>;
  displayName?: string;
}

const buildProviderComponent = <Value>(
  RawProvider: Provider<StoreSlot<Value> | undefined>,
) => {
  const ManagedWrapper = ({
    value,
    children,
  }: {
    value: Value;
    children: ReactNode;
  }) => {
    const valHolder = useRef(value);
    const snapHolder = useRef(0);
    const [pendingResolver, setPendingResolver] = useState<((v: Value) => void) | null>(null);
    if (pendingResolver) {
      pendingResolver(value);
      setPendingResolver(null);
    }
    const slotHolder = useRef<StoreSlot<Value> | undefined>(undefined);
    if (!slotHolder.current) {
      const subsCollection = new Set<Subscriber<Value>>();
      const notifyAndFlush = (effect: () => void, settings?: { suspense: boolean }) => {
        bulkUpdate(() => {
          snapHolder.current += 1;
          const notification: Parameters<Subscriber<Value>>[0] = {
            snap: snapHolder.current,
          };
          if (settings?.suspense) {
            notification.snap *= -1;
            notification.pending = new Promise<unknown>(fulfill => {
              setPendingResolver(() => (resolvedVal: Value) => {
                notification.val = resolvedVal;
                delete notification.pending;
                fulfill(resolvedVal);
              });
            });
          }
          subsCollection.forEach(subscriber => subscriber(notification));
          effect();
        });
      };
      slotHolder.current = {
        [DATA_SLOT]: {
          valRef: valHolder,
          snapRef: snapHolder,
          subsSet: subsCollection,
          schedule: notifyAndFlush,
        },
      };
    }
    safeUseLayoutEffect(() => {
      valHolder.current = value;
      snapHolder.current += 1;
      invokeWithNormalPriority(() => {
        (slotHolder.current as StoreSlot<Value>)[DATA_SLOT].subsSet.forEach(
          subscriber => {
            subscriber({ snap: snapHolder.current, val: value });
          },
        );
      });
    }, [value]);
    return createElement(
      RawProvider,
      { value: slotHolder.current },
      children,
    );
  };
  return ManagedWrapper;
};

/**
 * 创建支持 useManagedSelector 的 Context（须使用返回的 Provider）
 */
export function defineManagedContext<Value>(
  defaultValue: Value,
): IManagedContext<Value> {
  const context = createContextOrig<StoreSlot<Value>>({
    [DATA_SLOT]: {
      valRef: { current: defaultValue },
      snapRef: { current: -1 },
      subsSet: new Set(),
      schedule: f => f(),
    },
  });
  (
    context as unknown as {
      [PRIMITIVE_PROVIDER]: Provider<StoreSlot<Value> | undefined>;
    }
  )[PRIMITIVE_PROVIDER] = context.Provider;
  (context as unknown as IManagedContext<Value>).Provider = buildProviderComponent(
    context.Provider,
  );
  delete (context as { Consumer?: unknown }).Consumer;
  return context as unknown as IManagedContext<Value>;
}

/**
 * 按 selector 订阅 Context；仅当选中值 Object.is 变化时重渲染
 */
export function useManagedSelector<Value, Selected>(
  context: IManagedContext<Value>,
  selector: (value: Value) => Selected,
): Selected {
  const slot = useContextOrig(
    context as unknown as ContextOrig<StoreSlot<Value>>,
  )[DATA_SLOT];
  if (!slot) {
    throw new Error(
      'useManagedSelector requires the Provider from defineManagedContext (not React.createContext.Provider)',
    );
  }
  const {
    valRef: { current: rootVal },
    snapRef: { current: snapNum },
    subsSet: subsCollection,
  } = slot;
  const derived = selector(rootVal);
  const [pair, receive] = useReducer(
    (
      oldPair: readonly [Value, Selected],
      notification?: Parameters<Subscriber<Value>>[0],
    ) => {
      if (!notification) {
        return [rootVal, derived] as const;
      }
      if (notification.pending != null) {
        throw notification.pending;
      }
      if (notification.snap === snapNum) {
        if (Object.is(oldPair[1], derived)) {
          return oldPair;
        }
        return [rootVal, derived] as const;
      }
      try {
        if ('val' in notification) {
          if (Object.is(oldPair[0], notification.val)) {
            return oldPair;
          }
          const nextDerived = selector(notification.val);
          if (Object.is(oldPair[1], nextDerived)) {
            return oldPair;
          }
          return [notification.val, nextDerived] as const;
        }
      } catch {
        // stale selector
      }
      return [...oldPair] as const;
    },
    [rootVal, derived] as const,
  );
  safeUseLayoutEffect(() => {
    if (!Object.is(pair[1], derived)) {
      receive();
    }
  });
  safeUseLayoutEffect(() => {
    subsCollection.add(receive);
    return () => {
      subsCollection.delete(receive);
    };
  }, [subsCollection]);
  return pair[1];
}
